import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { Card, Btn, Input, Empty, Table, Td, Badge } from "../../components/UI";
import { CocoaSelect } from "../../components/CocoaSelect";
import { usePagination, PaginationBar } from "../../components/Pagination";
import { Search, Download, ShieldCheck, ArrowUpDown, Filter, Lock } from "lucide-react";
import { toast } from "sonner";

// Comprehensive audit report (audit item 71 + 72 + 90). Cannot be deleted:
// there is no DELETE endpoint on /audit-logs by design. Every row includes a
// retention indicator computed from the timestamp so admins can see how long
// each event will be retained. Filters, sort, search, and CSV export all
// server-side so we can scale past 500k rows without freezing the browser.

const ACTIONS = ["", "create", "update", "delete", "sign", "cosign", "amend", "unlock",
  "unlock_request", "unlock_approve", "unlock_deny", "view", "download", "export",
  "login", "logout", "revoke", "archive", "dry_run_purge"];

const RESOURCES = ["", "client", "chart_note", "progress_note", "treatment_plan",
  "assessment", "consent_form", "crisis_plan", "medication", "appointment",
  "intake_form", "task", "user", "auth", "message", "outcome_measure", "retention"];

const SORT_OPTIONS = [
  ["timestamp_desc", "Newest first"],
  ["timestamp_asc", "Oldest first"],
  ["user_email_asc", "User (A to Z)"],
  ["user_email_desc", "User (Z to A)"],
  ["resource_asc", "Resource (A to Z)"],
  ["action_asc", "Action (A to Z)"],
];

// Retention horizon in years for common resource types. Aligns with the
// Data Retention page (7 years for clinical, indefinite for audit / signatures).
const RETENTION_YEARS = {
  audit: -1, signature: -1,
  client: 7, chart_note: 7, progress_note: 7, treatment_plan: 7, assessment: 7,
  consent_form: 7, crisis_plan: 7, appointment: 7, intake_form: 7, medication: 7,
  outcome_measure: 7, task: 7, message: 7, user: 7,
};

function retentionRemaining(row) {
  if (!row.timestamp) return { text: "-", tone: "neutral" };
  const yrs = RETENTION_YEARS[row.resource];
  if (yrs === -1) return { text: "Retain indefinitely", tone: "indefinite" };
  const y = yrs ?? 7;
  const purge = dayjs(row.timestamp).add(y, "year");
  const daysLeft = purge.diff(dayjs(), "day");
  if (daysLeft < 0) return { text: `Eligible for purge (${-daysLeft} d overdue)`, tone: "overdue" };
  const remaining = purge.diff(dayjs(), "month");
  const months = remaining % 12;
  const years = Math.floor(remaining / 12);
  return { text: `Retain ${years} yr ${months} mo more`, tone: "active" };
}

export default function AuditReportSection() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("");
  const [resource, setResource] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [sort, setSort] = useState("timestamp_desc");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { const t = setTimeout(() => setSearch(searchInput), 300); return () => clearTimeout(t); }, [searchInput]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 500, sort };
    if (search) params.q = search;
    if (action) params.action = action;
    if (resource) params.resource = resource;
    if (userEmail) params.user_email = userEmail;
    if (since) params.since = since;
    if (until) params.until = until;
    api.get("/audit-logs", { params }).then((r) => {
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
      setLoading(false);
    }).catch((e) => { toast.error(apiError(e)); setLoading(false); });
  }, [search, action, resource, userEmail, since, until, sort]);

  const pg = usePagination(rows);

  const exportCsv = () => {
    const cols = ["timestamp", "user_email", "role", "ip", "resource", "resource_id", "action", "detail"];
    const csvHead = cols.join(",");
    const csvRows = rows.map((r) => cols.map((c) => {
      const v = r[c] == null ? "" : String(r[c]);
      return `"${v.replace(/"/g, '""')}"`;
    }).join(","));
    const blob = new Blob([csvHead + "\n" + csvRows.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `audit-report-${dayjs().format("YYYYMMDD-HHmm")}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const clearFilters = () => {
    setSearchInput(""); setSearch(""); setAction(""); setResource("");
    setUserEmail(""); setSince(""); setUntil(""); setSort("timestamp_desc");
  };

  return (
    <Card className="p-0 overflow-hidden" data-testid="settings-audit-report">
      <div className="border-b border-[#F1EEE7] px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-bold text-[#3E2723] flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#2E7D32]" /> Complete Audit Report
          </h3>
          <p className="mt-0.5 text-[11px] text-[#7B6157]">
            Every create, update, sign, view, download, and delete event across the tenant.
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-bold text-[#2E7D32]"><Lock size={9} /> Append-only, cannot be deleted</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="audit-report-toggle-filters" onClick={() => setShowFilters((s) => !s)}
            className="inline-flex items-center gap-1 rounded-full border border-[#E5DFD3] bg-white px-3 py-1.5 text-xs font-semibold text-[#4E342E] hover:bg-[#F5F2EB]">
            <Filter size={13} /> {showFilters ? "Hide filters" : "Filters"}
          </button>
          <Btn variant="outline" data-testid="audit-report-export-csv" onClick={exportCsv} className="px-3 py-1.5 text-xs">
            <Download size={13} /> Export CSV
          </Btn>
        </div>
      </div>

      <div className="border-b border-[#F1EEE7] px-5 py-3 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[280px] flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
          <Input data-testid="audit-report-search" className="pl-9 h-9" placeholder="Search email, resource, resource ID, action, or detail…"
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <CocoaSelect testId="audit-report-sort" className="w-44 h-9" value={sort} onChange={setSort}
          options={SORT_OPTIONS} placeholder="Sort by" />
        <span data-testid="audit-report-count" className="text-xs font-semibold text-[#7B6157] rounded-full bg-[#F5F2EB] px-3 py-1.5">
          {loading ? "Loading…" : `${total} events`}
        </span>
      </div>

      {showFilters && (
        <div className="border-b border-[#F1EEE7] px-5 py-3 grid gap-3 md:grid-cols-5">
          <CocoaSelect testId="audit-report-action" className="w-full" value={action} onChange={setAction}
            options={ACTIONS.map((a) => [a, a || "All actions"])} placeholder="All actions" />
          <CocoaSelect testId="audit-report-resource" className="w-full" value={resource} onChange={setResource}
            options={RESOURCES.map((r) => [r, r || "All resources"])} placeholder="All resources" />
          <Input data-testid="audit-report-user-email" className="h-9" placeholder="User email"
            value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
          <Input data-testid="audit-report-since" type="date" className="h-9" value={since ? since.slice(0, 10) : ""}
            onChange={(e) => setSince(e.target.value ? `${e.target.value}T00:00:00` : "")} />
          <Input data-testid="audit-report-until" type="date" className="h-9" value={until ? until.slice(0, 10) : ""}
            onChange={(e) => setUntil(e.target.value ? `${e.target.value}T23:59:59` : "")} />
          <div className="md:col-span-5 flex justify-end">
            <Btn variant="outline" data-testid="audit-report-clear" onClick={clearFilters} className="px-3 py-1 text-xs">Clear filters</Btn>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        {rows.length === 0 && !loading ? <Empty text="No audit events match these filters" /> : (
          <Table headers={["Timestamp (UTC)", "User", "Role", "IP", "Resource", "Resource ID", "Action", "Detail", "Retention"]}>
            {pg.pageRows.map((r) => {
              const ret = retentionRemaining(r);
              return (
                <tr key={r.id} className="border-b border-[#EFEBE0] last:border-0" data-testid={`audit-row-${r.id}`}>
                  <Td><span className="font-mono text-[11px] tabular-nums">{(r.timestamp || "").replace("T", " ").slice(0, 19)}</span></Td>
                  <Td><span className="text-sm font-semibold text-[#3E2723]">{r.user_email || "-"}</span></Td>
                  <Td><Badge value={r.role || "-"} /></Td>
                  <Td><span className="font-mono text-[10px]">{r.ip || "-"}</span></Td>
                  <Td><span className="text-xs font-semibold uppercase tracking-wider text-[#5D4037]">{r.resource || "-"}</span></Td>
                  <Td><span className="font-mono text-[10px] text-[#6D4C41]">{(r.resource_id || "-").slice(0, 12)}</span></Td>
                  <Td><span className="text-xs font-bold text-[#4E342E]">{r.action || "-"}</span></Td>
                  <Td className="max-w-xs"><span className="text-xs text-[#6D4C41] break-words">{r.detail || "-"}</span></Td>
                  <Td>
                    <span data-testid={`audit-retention-${r.id}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      ret.tone === "indefinite" ? "bg-[#E3F2FD] text-[#1565C0]"
                      : ret.tone === "overdue" ? "bg-[#FFEBEE] text-[#C62828]"
                      : "bg-[#F5F2EB] text-[#4E342E]"
                    }`}>{ret.text}</span>
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </div>

      <PaginationBar {...pg} testId="audit-report-pagination" />

      <div className="border-t border-[#F1EEE7] px-5 py-3 flex flex-wrap items-center justify-between gap-3 text-[10px] text-[#7B6157]">
        <div className="flex items-center gap-1"><ArrowUpDown size={11} /> Server-side sort, filter, and search. Cap: 500 rows per page.</div>
        <div>Retention follows HIPAA § 164.316(b)(2)(i) plus 7-year clinical minimum. Audit log itself is retained indefinitely.</div>
      </div>
    </Card>
  );
}
